import { Button, Label, Modal, Textarea, TextInput } from "flowbite-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Group } from "@/sharedTypes";

interface GroupFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Group) => void;
  mode?: "create" | "edit";
  initialData?: Group;
}

const GroupFormDialog = ({
  open,
  onClose,
  onSubmit,
  mode = "create",
  initialData,
}: GroupFormDialogProps) => {
  const { t } = useTranslation(["group", "common"]);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Group>();

  useEffect(() => {
    if (open) {
        if (initialData) {
            reset(initialData);
        } else {
            reset({ group_name: "", group_description: "" } as Group);
        }
    }
  }, [initialData, reset, open]);

  const onFormSubmit = (data: Group) => {
    onSubmit(data);
  };

  return (
    <Modal show={open} onClose={onClose} size="md">
      <Modal.Header>{mode === "edit" ? t("group:editGroup") : t("group:createGroup")}</Modal.Header>
      <Modal.Body>
        <form onSubmit={(e) => { e.stopPropagation(); handleSubmit(onFormSubmit)(e); }} className="space-y-4">
          <div>
            <div className="mb-2 block">
              <Label htmlFor="group_name" value={t("group:groupName")} />
            </div>
            <TextInput
              id="group_name"
              placeholder={t("group:enterGroupName")}
              {...register("group_name", { required: t("group:groupNameRequired") })}
              color={errors.group_name ? "failure" : undefined}
              helperText={
                errors.group_name && (
                  <span className="font-medium">{errors.group_name.message}</span>
                )
              }
            />
          </div>

          <div>
            <div className="mb-2 block">
              <Label htmlFor="group_description" value={t("group:description")} />
            </div>
            <Textarea
              id="group_description"
              placeholder={t("group:enterGroupDescription")}
              {...register("group_description", { required: t("group:groupDescriptionRequired") })}
              color={errors.group_description ? "failure" : undefined}
              helperText={
                errors.group_description && (
                  <span className="font-medium">{errors.group_description.message}</span>
                )
              }
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button color="failure" onClick={onClose}>
              {t("common:cancel")}
            </Button>
            <Button type="submit">
              {mode === "edit" ? t("common:saveChanges") : t("group:createGroup")}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default GroupFormDialog;
